import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer, concat, from } from 'rxjs';
import { switchMap, takeWhile, map, tap, catchError } from 'rxjs/operators';
import {
  IProjectExportImportService,
  ExportProgress,
  ImportProgress,
  HpcExportResult,
  HpcImportResult,
  IFProjManifest,
  IFPROJ_MAGIC,
  IFPROJ_MAGIC_LENGTH,
} from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';
import { readHpcResultJobId } from './hpc-results-manifest';
import { readZipCentralDirectory, readZipEntryBytes } from './zip-range-reader';

@Injectable()
export class ProjectExportImportCloudService implements IProjectExportImportService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);

  /** Stored File object from browser file picker (for cloud import). */
  private _pendingFile: File | null = null;

  private get baseUrl(): string {
    return `${this.config.apiBaseUrl}/project`;
  }

  /** Store a File object for use in readManifest / importProject. */
  setImportFile(file: File): void {
    this._pendingFile = file;
  }

  exportProject(projectId: string, _savePath?: string): Observable<ExportProgress> {
    return this.http
      .post<{ exportId: string }>(`${this.baseUrl}/${projectId}/export`, {})
      .pipe(
        switchMap(() =>
          concat(
            of<ExportProgress>({
              phase: 'preparing',
              percent: 10,
              message: 'Export started...',
            }),
            this.pollExportStatus(projectId),
          ),
        ),
        catchError(() =>
          of<ExportProgress>({
            phase: 'error',
            percent: 0,
            message: 'Failed to start export.',
          }),
        ),
      );
  }

  exportForHpc(projectId: string, runType: 'fea' | 'optimize' = 'optimize'): Observable<HpcExportResult> {
    // The backend builds the bundle in-process and returns a presigned URL, so
    // this is a single request (unlike the async .ifproj export above). Auth is
    // applied automatically by the JWT interceptor. runType selects the pipeline
    // baked into config.xml + request.json (optimize vs FEA).
    return this.http.post<HpcExportResult>(
      `${this.baseUrl}/${projectId}/export/hpc`,
      { runType },
    );
  }

  importForHpc(
    projectId: string,
    file: File,
    allowedJobIds?: readonly string[],
  ): Observable<HpcImportResult> {
    // Fail fast on a bundle meant for another project. The backend is the
    // authority (importFromHpc), but it can only judge once the file is in S3 —
    // so picking the wrong project meant waiting out a full multi-hundred-MB
    // upload before the rejection appeared, long enough to look like a hang.
    // Peeking output/manifest.json locally answers in milliseconds and skips the
    // upload entirely. A jobId we can't read is not a rejection: readHpcResultJobId
    // returns null and the server decides as before.
    const allowed = allowedJobIds?.length ? allowedJobIds : [projectId];
    return from(readHpcResultJobId(file)).pipe(
      switchMap((jobId) => {
        if (jobId && !allowed.includes(jobId)) {
          // Shaped like the backend's 400 so the caller's existing error
          // handling renders the message unchanged.
          return throwError(() => ({
            status: 400,
            error: {
              message:
                `These HPC results belong to a different project (${jobId}) — nothing was uploaded. ` +
                'Open that project and import them there.',
            },
          }));
        }
        return this.uploadHpcResults(projectId, file);
      }),
    );
  }

  private uploadHpcResults(projectId: string, file: File): Observable<HpcImportResult> {
    // Mirror of importProject's presign -> PUT -> kickoff, against the HPC
    // import endpoints. No polling: the backend lands the results into the
    // project's S3 prefix and finalizes the project asynchronously, and the
    // row updates over the websocket. runType is read from the bundle's
    // manifest server-side, so the client doesn't send it.
    return this.http
      .post<{
        url: string;
        s3Key: string;
        importId: string;
        requiredHeaders?: Record<string, string>;
      }>(`${this.baseUrl}/${projectId}/import/hpc/presign`, {})
      .pipe(
        switchMap(({ url, s3Key, requiredHeaders }) =>
          from(
            // S3 rejects the PUT unless every header the URL was signed with is
            // sent, so send exactly what the presign says it signed.
            fetch(url, {
              method: 'PUT',
              body: file,
              headers: { ...requiredHeaders, 'Content-Type': 'application/zip' },
            }),
          ).pipe(
            switchMap((response) => {
              if (!response.ok) throw new Error('Upload failed');
              return this.http.post<HpcImportResult>(
                `${this.baseUrl}/${projectId}/import/hpc`,
                { s3Key },
              );
            }),
          ),
        ),
      );
  }

  importProject(fileOrKey: string, categoryId?: string, targetUserId?: string): Observable<ImportProgress> {
    const file = this._pendingFile;

    if (!file) {
      return throwError(() => new Error('No file selected'));
    }

    return concat(
      of<ImportProgress>({
        phase: 'validating',
        percent: 5,
        message: 'Getting upload URL...',
      }),
      // Step 1: Get presigned upload URL
      this.http
        .post<{ url: string; s3Key: string; importId: string }>(
          `${this.baseUrl}/import/presign`,
          { filename: file.name },
        )
        .pipe(
          switchMap(({ url, s3Key, importId }) =>
            concat(
              of<ImportProgress>({
                phase: 'validating',
                percent: 15,
                message: 'Uploading file...',
              }),
              // Step 2: Upload file to S3
              from(
                fetch(url, {
                  method: 'PUT',
                  body: file,
                  // SSE-KMS header required by the org RCP on the upload bucket.
                  headers: { 'Content-Type': 'application/octet-stream', 'x-amz-server-side-encryption': 'aws:kms' },
                }),
              ).pipe(
                switchMap((response) => {
                  if (!response.ok) throw new Error('Upload failed');
                  return concat(
                    of<ImportProgress>({
                      phase: 'extracting',
                      percent: 40,
                      message: 'Processing...',
                    }),
                    // Step 3: Trigger server-side import
                    this.http
                      .post<{ importId: string }>(
                        `${this.baseUrl}/import/start`,
                        { s3Key, categoryId, importId, target_user_id: targetUserId },
                      )
                      .pipe(
                        tap(() => {
                          this._pendingFile = null;
                        }),
                        switchMap(() => this.pollImportStatus(importId)),
                        catchError(() =>
                          of<ImportProgress>({
                            phase: 'error',
                            percent: 0,
                            message: 'Failed to start import.',
                          }),
                        ),
                      ),
                  );
                }),
                catchError(() =>
                  of<ImportProgress>({
                    phase: 'error',
                    percent: 0,
                    message: 'Failed to upload file.',
                  }),
                ),
              ),
            ),
          ),
          catchError(() =>
            of<ImportProgress>({
              phase: 'error',
              percent: 0,
              message: 'Failed to get upload URL.',
            }),
          ),
        ),
    );
  }

  readManifest(filePath: string): Observable<IFProjManifest> {
    const file = this._pendingFile;
    if (!file) {
      return throwError(() => new Error('No file selected'));
    }

    return new Observable<IFProjManifest>((subscriber) => {
      this.extractManifestFromFile(file)
        .then((manifest) => {
          subscriber.next(manifest);
          subscriber.complete();
        })
        .catch((err) => subscriber.error(err));
    });
  }

  validateFile(filePath: string): Observable<boolean> {
    const file = this._pendingFile;
    if (!file) {
      return of(false);
    }

    return new Observable<boolean>((subscriber) => {
      this.validateHeader(file)
        .then((valid) => {
          subscriber.next(valid);
          subscriber.complete();
        })
        .catch(() => {
          subscriber.next(false);
          subscriber.complete();
        });
    });
  }

  pickExportPath(_projectName: string): Observable<string | null> {
    // Cloud uses browser download — no path picking needed
    return of(null);
  }

  pickImportFile(): Observable<string | null> {
    // Cloud uses <input type="file"> in template — not this method
    return of(null);
  }

  // ── Private helpers ────────────────────────────────────────────────

  private async validateHeader(file: File): Promise<boolean> {
    if (file.size < IFPROJ_MAGIC_LENGTH) return false;

    const headerSlice = file.slice(0, IFPROJ_MAGIC_LENGTH);
    const buffer = await headerSlice.arrayBuffer();
    const header = new Uint8Array(buffer);

    for (let i = 0; i < IFPROJ_MAGIC_LENGTH; i++) {
      if (header[i] !== IFPROJ_MAGIC[i]) return false;
    }
    return true;
  }

  /**
   * Extract manifest.json from the .ifproj file by reading only the ranges it
   * needs. The archive can exceed the 2 GiB a browser will read into one
   * ArrayBuffer, so the file is never loaded whole.
   */
  private async extractManifestFromFile(file: File): Promise<IFProjManifest> {
    if (!(await this.validateHeader(file))) {
      throw new Error('Not a valid .ifproj file');
    }

    // Zip offsets are relative to the archive, which starts after the header.
    const zip = file.slice(IFPROJ_MAGIC_LENGTH);
    const entries = await readZipCentralDirectory(zip);
    if (!entries) {
      throw new Error('Invalid archive: End of Central Directory not found');
    }

    const entry = entries.find((e) => e.name === 'manifest.json');
    if (!entry) {
      throw new Error('manifest.json not found in archive');
    }

    const bytes = await readZipEntryBytes(zip, entry);
    if (!bytes) {
      throw new Error('Unsupported compression method in archive');
    }
    return JSON.parse(new TextDecoder().decode(bytes)) as IFProjManifest;
  }

  /** Poll export status every 3 seconds until complete or error. */
  private pollExportStatus(projectId: string): Observable<ExportProgress> {
    return timer(0, 3000).pipe(
      switchMap(() =>
        this.http.get<{
          status: string;
          percent: number;
          message: string;
          downloadUrl?: string;
        }>(`${this.baseUrl}/${projectId}/export/status`),
      ),
      map((res): ExportProgress => {
        if (res.status === 'complete' && res.downloadUrl) {
          return {
            phase: 'complete',
            percent: 100,
            message: 'Export complete',
            downloadUrl: res.downloadUrl,
          };
        } else if (res.status === 'error') {
          return {
            phase: 'error',
            percent: 0,
            message: res.message || 'Export failed',
          };
        } else {
          return {
            phase: 'packaging',
            percent: res.percent,
            message: res.message,
          };
        }
      }),
      takeWhile((p) => p.phase !== 'complete' && p.phase !== 'error', true),
    );
  }

  /** Poll import status every 3 seconds until complete or error. */
  private pollImportStatus(importId: string): Observable<ImportProgress> {
    return timer(0, 3000).pipe(
      switchMap(() =>
        this.http.get<{
          status: string;
          percent: number;
          message: string;
          projectId?: string;
        }>(`${this.baseUrl}/import/${importId}/status`),
      ),
      map((res): ImportProgress => {
        if (res.status === 'complete') {
          return {
            phase: 'complete',
            percent: 100,
            message: 'Import complete',
          };
        } else if (res.status === 'error') {
          return {
            phase: 'error',
            percent: 0,
            message: 'Import failed',
          };
        } else {
          return {
            phase: 'extracting',
            percent: res.percent,
            message: res.message,
          };
        }
      }),
      takeWhile((p) => p.phase !== 'complete' && p.phase !== 'error', true),
    );
  }
}
